import { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';

import { ILegalEntityLookup } from 'APIPackage/FnsModule/interfaces/ILegalEntityLookup';
import { MspCodesEnum } from 'APIPackage/KnmModule/enums/MspCodesEnum';
import { IKnmCheckSubjectResult } from 'APIPackage/KnmModule/interfaces/IKnmCheckSubjectResult';
import {
  getOrganizationOgrnLabelBlockId,
  getOrganizationInnLabelBlockId,
  getOrganizationNameBlockId,
  getOrganizationOkvedsBlockId,
  getOrganizationOkvedBlockId,
  getOrganizationKppLabelBlockId,
  getOrganizationRafpNzaLabelBlockId,
} from 'KnmErknmPackage/MainModule/constants/knmErknmFieldsElementsIds';
import { IKnmErknmFormSubject } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormSubject';
import { KnmOkveds } from 'KnmPackage/MainModule/components/KnmOkveds';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import {
  getKnmFormRef,
  getKnmIsChangeSubjectTypeFromActionsAvailable,
  getKnmIsEditableInStatusCommentsExist,
  getKnmIsWatchOnly,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { InspectionResultFieldEnum } from 'APIPackage/CatalogsModule/enums/InspectionResultFieldEnum';
import { ErknmInspectionResultMessage } from 'KnmErknmPackage/MainModule/components/ErknmInspectionResultMessageMessage';
import { InspectionResultActionTypeEnum } from 'APIPackage/CatalogsModule/enums/InspectionResultActionTypeEnum';
import { IKnmErknmForm } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmForm';
import { createTypedUseFormValue } from 'hooks/useTypedFormField';
import { KnmReasonTypeNameEnum } from 'KnmPackage/MainModule/enums/KnmReasonTypeNameEnum';
import { removeSubjectErvkObjects } from 'KnmPackage/MainModule/common/removeSubjectErvkObjects';

import styles from './ErknmSubjectItem.module.scss';
import { ErknmSubjectInfo, KnmSubjectTypesEnum } from './ErknmSubjectInfo';
import { ErknmRegistryCheck } from './ErknmRegistryCheck';
import { KnmCollapse } from '../../../KnmPackage/MainModule/components/KnmCollapse';
import { IDictionarySelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { ITargetEntity } from '../../../APIPackage/DictionariesModule/interfaces/ITargetEntity';
import { RafpRequestStatusTypeEnum } from '../../../APIPackage/KnmModule/enums/RafpRequestStatusTypeEnum';
import { KnmSonkoField } from '../../../KnmPackage/MainModule/components/KnmSonkoField';
import { KnmSubjectNkoInfo } from '../../../KnmPackage/MainModule/components/KnmSubjectNkoInfo';
import { KnmSubjectItInfo } from '../../../KnmPackage/MainModule/components/KnmSubjectItInfo';
import { IKnmSubject248ItAccreditationResponse } from '../../../APIPackage/KnmModule/interfaces/IKnmSubject248ItAccreditationResponse';
import { IInspectionResult } from '../../../APIPackage/CatalogsModule/interfaces/IInspectionResult';
import { KnmMspField } from '../../../KnmPackage/MainModule/components/KnmMspField';
import { checkHasErrorInCollapseBlock } from '../../../functions/checkHasErrorInCollapseBlock';

interface IErknmSubjectItemProps {
  checkSubjectResult: IKnmCheckSubjectResult | null;
  disableFieldByCompleteStatuses: boolean;
  disableFieldByInProcessStatuses: boolean;
  disabledRemoveObject?: boolean;
  ervkId: string | undefined;
  firstOrganizationName: string | undefined;
  id: string;
  inn: string | undefined;
  inspectionResults: IInspectionResult[] | undefined;
  isAutocomplete: IKnmErknmFormSubject['isAutocomplete'];
  isCollapsing: boolean;
  isConnectedToErvkObject: boolean;
  isFilledFromLink: boolean;
  isMultiFields: boolean;
  isNew: boolean;
  isNewOrganizationTemplate?: boolean;
  isPresentInItRegistry: boolean | undefined;
  isSonko: boolean | undefined;
  itAccreditationResponse: IKnmSubject248ItAccreditationResponse | undefined;
  kpp: string | undefined;
  mspCode: MspCodesEnum | undefined;
  mspRegistryDate: IKnmErknmFormSubject['mspRegistryDate'] | undefined;
  name: string | undefined;
  ogrn: string | undefined;
  okveds: IKnmErknmFormSubject['okveds'] | undefined;
  organizationsForSelect?: IKnmErknmFormSubject[];
  rafpNza: string | undefined;
  smevItAccreditationRequestStatus: string | undefined;
  smevRequestStatus: RafpRequestStatusTypeEnum | undefined;
  snils: string | undefined;
  subjectType: IDictionarySelectOption<ITargetEntity> | undefined;
  watchOnlyExtended: boolean;
}

interface IErknmSubjectItemStoreProps {
  formRef: IKnmFormRef | undefined;
  isChangeSubjectTypeFromActionsAvailable: boolean;
  isEditableInStatusCommentsExist: boolean;
  isWatchOnly: boolean;
}

const useFieldValue = createTypedUseFormValue<IKnmErknmForm>();

const ErknmSubjectItemWithoutStore = ({
  checkSubjectResult,
  disableFieldByCompleteStatuses,
  disableFieldByInProcessStatuses,
  disabledRemoveObject,
  ervkId,
  firstOrganizationName,
  id,
  inn,
  inspectionResults,
  isAutocomplete,
  isCollapsing,
  isConnectedToErvkObject,
  isFilledFromLink,
  isMultiFields,
  isNew,
  isNewOrganizationTemplate,
  isPresentInItRegistry,
  isSonko,
  itAccreditationResponse,
  kpp,
  mspCode,
  mspRegistryDate,
  name,
  ogrn,
  okveds,
  organizationsForSelect,
  rafpNza,
  smevItAccreditationRequestStatus,
  smevRequestStatus,
  snils,
  subjectType,
  watchOnlyExtended,
  formRef,
  isChangeSubjectTypeFromActionsAvailable,
  isEditableInStatusCommentsExist,
  isWatchOnly,
}: IErknmSubjectItemProps & IErknmSubjectItemStoreProps) => {
  const [isKnmCollapseOpened, setIsKnmCollapseOpened] = useState<boolean>(true);
  const [lookupData, setLookupData] = useState<ILegalEntityLookup | boolean | undefined>(undefined);
  const [checkByInnMessage, setCheckByInnMessage] = useState<string | undefined>(undefined);

  const isImpossibilityOfPrevious =
    useFieldValue('reasonsErknm')?.[0]?.type?.name === KnmReasonTypeNameEnum.IMPOSSIBILITY_OF_PREVIOUS;

  const getOkvedBlockId = useCallback((okvedId: string) => getOrganizationOkvedBlockId(id, okvedId), [id]);

  const remove = formRef?.current.mutators.remove;

  const handleSetSubjectResult = useCallback(
    (result: IKnmCheckSubjectResult | null) => {
      formRef?.current.change(`${name}.checkSubjectResult` as keyof IKnmForm, result);
      setLookupData(false);
    },
    [formRef, name],
  );

  /**
   * Признак запрета - связь КЛ с объектом из ЕРВК, такой КЛ добавляется и удаляется
   * только через ОБК. Предзаполнение по ссылке удалению не мешает (4.1.30).
   */
  const isRemoveErknmSubjectItemEnabled =
    ((!watchOnlyExtended && !isConnectedToErvkObject) || isEditableInStatusCommentsExist) && !disabledRemoveObject;

  const makeRemoveErknmSubjectItemHandler = () => () => {
    if (isRemoveErknmSubjectItemEnabled && remove) {
      remove('organizations', 0) && formRef?.current?.change('isSubjectDeleted', true);

      removeSubjectErvkObjects({ formRef, ervkId, objectsFieldName: 'objectsErknm' });
    }
  };

  const renderSubjectInfo = () => {
    const IROkveds = inspectionResults?.find(
      i => i.inspectedField === InspectionResultFieldEnum.ORGANIZATION_FNS_OKVED,
    );
    const IRNko = inspectionResults?.find(
      i => i.inspectedField === InspectionResultFieldEnum.FOREIGN_AGENT_NKO_REGISTRY,
    );

    const subjectInfo = () => (
      <>
        {/* Блок информации о контроллируемом лице */}
        <div className={styles.Wrapper} id={!isCollapsing ? id : undefined}>
          <ErknmSubjectInfo
            name={name}
            isWatchOnly={isWatchOnly || !!ervkId || isFilledFromLink}
            isAutocomplete={isAutocomplete}
            inn={inn}
            innLabelBlockId={getOrganizationInnLabelBlockId(id)}
            ogrn={ogrn}
            ogrnLabelBlockId={getOrganizationOgrnLabelBlockId(id)}
            firstOrganizationName={firstOrganizationName}
            organizationNameBlockId={getOrganizationNameBlockId(id)}
            subjectResult={checkSubjectResult}
            isExtended
            lookupData={lookupData}
            setLookupData={setLookupData}
            subjectType={subjectType}
            kppLabelBlockId={getOrganizationKppLabelBlockId(id)}
            rafpLabelBlockId={getOrganizationRafpNzaLabelBlockId(id)}
            kpp={kpp}
            smevRequestStatus={smevRequestStatus}
            rafpNza={rafpNza}
            snils={snils}
            isNewOrganizationTemplate={isNewOrganizationTemplate}
            inspectionResults={inspectionResults}
            checkSubjectResult={checkSubjectResult}
            ervkId={ervkId}
            setCheckByInnMessage={setCheckByInnMessage}
            disabledByChose={!!organizationsForSelect}
            isChangeSubjectTypeFromActionsAvailable={isChangeSubjectTypeFromActionsAvailable}
          />

          {(subjectType?.label === KnmSubjectTypesEnum.UR ||
            subjectType?.label === KnmSubjectTypesEnum.IP ||
            subjectType?.label === KnmSubjectTypesEnum.FIUR) && (
            <KnmOkveds
              name={`${name}.okveds`}
              subjectData={{ inn, ogrn }}
              watchOnly={
                watchOnlyExtended ||
                disableFieldByCompleteStatuses ||
                disableFieldByInProcessStatuses ||
                isImpossibilityOfPrevious ||
                isFilledFromLink
              }
              okveds={okveds}
              organizationAutocomplete={isAutocomplete}
              blockId={getOrganizationOkvedsBlockId(id)}
              getOkvedBlockId={getOkvedBlockId}
              inspectionResult={IROkveds}
              checkSubjectResult={checkSubjectResult}
            />
          )}

          {/* Блок информации о наличии контролируемого лица в реестре НКО-иноагентов */}
          {subjectType?.label === KnmSubjectTypesEnum.UR && (
            <KnmSubjectNkoInfo
              id={id}
              name={name}
              ogrn={ogrn}
              ervkId={ervkId}
              inspectionResult={IRNko}
              checkByInnMessage={checkByInnMessage}
              formRef={formRef}
              isShowMessages={true}
            />
          )}

          {/* Блок информации о наличии контролируемого лица в реестре IT-компаний */}
          {(subjectType?.label === KnmSubjectTypesEnum.UR || subjectType?.label === KnmSubjectTypesEnum.FIUR) && (
            <KnmSubjectItInfo
              name={name}
              inn={inn}
              inspectionResults={inspectionResults}
              isPresentInItRegistry={isPresentInItRegistry}
              itAccreditationResponse={itAccreditationResponse}
              smevItAccreditationRequestStatus={smevItAccreditationRequestStatus}
              checkByInnMessage={checkByInnMessage}
              isShowMessages={true}
              ervkId={ervkId}
            />
          )}
        </div>
      </>
    );

    if (isMultiFields) {
      return subjectInfo();
    }

    const inspectionResultAvailable = inspectionResults?.find(
      i =>
        i.inspectedField === InspectionResultFieldEnum.ORGANIZATION_ERVK_NAME ||
        i.inspectedField === InspectionResultFieldEnum.ORGANIZATION_FNS_NOT_FOUND ||
        i.inspectedField === InspectionResultFieldEnum.ORGANIZATION_ERVK_ADDRESS,
    );

    const generateCollapseName = useCallback((inspectionResult: IInspectionResult | undefined) => {
      if (inspectionResult) {
        return <ErknmInspectionResultMessage inspectionResult={inspectionResult} />;
      }

      return undefined;
    }, []);

    const hasError = name ? checkHasErrorInCollapseBlock(formRef, name) : false;

    return (
      <KnmCollapse
        id={id}
        theme="fields"
        title={'Контролируемое лицо'}
        isNew={isNew}
        opened={isNew || !isClosedByManyOrgs}
        onClose={isRemoveErknmSubjectItemEnabled ? makeRemoveErknmSubjectItemHandler() : undefined}
        key={name}
        subtitle={generateCollapseName(inspectionResultAvailable)}
        headerClassName={cn([
          {
            [styles.BorderYellow]:
              inspectionResultAvailable &&
              inspectionResultAvailable.resultActionType === InspectionResultActionTypeEnum.LIGHT_YELLOW,
            [styles.BorderRed]:
              inspectionResultAvailable &&
              inspectionResultAvailable.resultActionType === InspectionResultActionTypeEnum.LIGHT_RED,
          },
        ])}
        error={isKnmCollapseOpened && hasError}
        changeOpenedCallback={setIsKnmCollapseOpened}
      >
        {subjectInfo()}
      </KnmCollapse>
    );
  };

  const IRIsSonko = inspectionResults?.find(i => i.inspectedField === InspectionResultFieldEnum.SONKO_NKO_REGISTRY);
  const IRIsMsp = inspectionResults?.find(i => i.inspectedField === InspectionResultFieldEnum.MSP_REGISTRY);
  // @ts-ignore
  const organizations = organizationsForSelect || formRef?.current.getFieldState('organizations')?.value;

  // Если слишком много КЛ - форма с открытыми блоками грузится слишком долго, поэтому по-умолчанию блоки свернуты, если их много
  const isClosedByManyOrgs = !!(organizations && organizations.length > 10);

  return (
    <>
      {organizations && (
        <div className={styles.Wrapper} id={!isCollapsing ? id : undefined}>
          {renderSubjectInfo()}

          <KnmMspField
            id={`${id}.mspCode`}
            name={`${name}.mspCode`}
            value={mspCode}
            inspectionResult={IRIsMsp}
            checkSubjectResult={checkSubjectResult}
            ervkId={ervkId}
            inn={inn}
            mspRegistryDate={mspRegistryDate}
            formRef={formRef}
            isShowMessages={subjectType?.label !== KnmSubjectTypesEnum.FIZ}
          />

          <KnmSonkoField
            inn={inn}
            ogrn={ogrn}
            name={`${name}.isSonko`}
            subjectType={subjectType}
            value={isSonko}
            inspectionResult={IRIsSonko}
            id={`${id}.isSonko`}
            checkByInnMessage={!ervkId ? checkByInnMessage : undefined}
            formRef={formRef}
            isShowMessages={true}
          />

          <ErknmRegistryCheck
            className={styles.RegistryCheckBlock}
            initialCheckSubjectResult={checkSubjectResult}
            setSubjectResult={handleSetSubjectResult}
          />
        </div>
      )}
    </>
  );
};

const mapStateToProps = (state: IRootState): IErknmSubjectItemStoreProps => ({
  formRef: getKnmFormRef(state),
  isWatchOnly: getKnmIsWatchOnly(state),
  isEditableInStatusCommentsExist: getKnmIsEditableInStatusCommentsExist(state),
  isChangeSubjectTypeFromActionsAvailable: getKnmIsChangeSubjectTypeFromActionsAvailable(state),
});

export const ErknmSubjectItem = connect(mapStateToProps)(ErknmSubjectItemWithoutStore);
